import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, IconButton, useMediaQuery, useTheme, Checkbox, FormControlLabel, Autocomplete, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';

const QuestionIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '500px', // Positioned above ReportIcon
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const QuestionIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)', // Green gradient
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(102, 187, 106, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(102, 187, 106, 0.5)',
  },
}));

const QuestionTooltip = styled(Box)(({ theme, isMobile }) => ({
  position: 'absolute',
  bottom: isMobile ? '15px' : '10px',
  right: isMobile ? '70px' : '80px',
  width: isMobile ? '260px' : '280px',
  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', // Green gradient
  border: '2px solid #66bb6a', // Green border
  borderRadius: '12px',
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  zIndex: 10014,
  boxShadow: '0 12px 40px rgba(102, 187, 106, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: isMobile ? '25px' : '20px',
    right: isMobile ? '-8px' : '-10px',
    width: '0',
    height: '0',
    borderTop: `${isMobile ? '8px' : '10px'} solid transparent`,
    borderBottom: `${isMobile ? '8px' : '10px'} solid transparent`,
    borderLeft: `${isMobile ? '8px' : '10px'} solid #4caf50`, // Green arrow pointing right
  },
}));

// List of countries for the dropdown
const COUNTRIES = [
  'Deutschland',
  'Österreich',
  'Schweiz',
  'Albanien',
  'Andorra',
  'Belarus',
  'Belgien',
  'Bosnien und Herzegowina',
  'Bulgarien',
  'Dänemark',
  'Estland',
  'Finnland',
  'Frankreich',
  'Griechenland',
  'Irland',
  'Island',
  'Italien',
  'Kroatien',
  'Lettland',
  'Liechtenstein',
  'Litauen',
  'Luxemburg',
  'Malta',
  'Moldau',
  'Monaco',
  'Montenegro',
  'Niederlande',
  'Nordmazedonien',
  'Norwegen',
  'Polen',
  'Portugal',
  'Rumänien',
  'Russland',
  'San Marino',
  'Schweden',
  'Serbien',
  'Slowakei',
  'Slowenien',
  'Spanien',
  'Tschechien',
  'Ukraine',
  'Ungarn',
  'Vatikanstadt',
  'Vereinigtes Königreich',
  'Zypern',
  'Afghanistan',
  'Ägypten',
  'Algerien',
  'Argentinien',
  'Australien',
  'Bangladesch',
  'Brasilien',
  'China',
  'Indien',
  'Indonesien',
  'Iran',
  'Irak',
  'Israel',
  'Japan',
  'Kanada',
  'Mexiko',
  'Nigeria',
  'Pakistan',
  'Philippinen',
  'Südafrika',
  'Südkorea',
  'Türkei',
  'USA',
  'Vietnam',
];

// Function to get country flag emoji
const getCountryFlag = (country) => {
  if (!country) {
    return '🌍'; // World citizen flag
  }
  
  const countryFlags = {
    'Deutschland': '🇩🇪',
    'Germany': '🇩🇪',
    'Österreich': '🇦🇹',
    'Austria': '🇦🇹',
    'Schweiz': '🇨🇭',
    'Switzerland': '🇨🇭',
    'Frankreich': '🇫🇷',
    'France': '🇫🇷',
    'Italien': '🇮🇹',
    'Italy': '🇮🇹',
    'Spanien': '🇪🇸',
    'Spain': '🇪🇸',
    'Niederlande': '🇳🇱',
    'Netherlands': '🇳🇱',
    'Belgien': '🇧🇪',
    'Belgium': '🇧🇪',
    'Polen': '🇵🇱',
    'Poland': '🇵🇱',
    'Tschechien': '🇨🇿',
    'Czech Republic': '🇨🇿',
    'Dänemark': '🇩🇰',
    'Denmark': '🇩🇰',
    'Schweden': '🇸🇪',
    'Sweden': '🇸🇪',
    'Norwegen': '🇳🇴',
    'Norway': '🇳🇴',
    'Finnland': '🇫🇮',
    'Finland': '🇫🇮',
    'Vereinigtes Königreich': '🇬🇧',
    'United Kingdom': '🇬🇧',
    'UK': '🇬🇧',
    'Irland': '🇮🇪',
    'Ireland': '🇮🇪',
    'Portugal': '🇵🇹',
    'Griechenland': '🇬🇷',
    'Greece': '🇬🇷',
    'Luxemburg': '🇱🇺',
    'Luxembourg': '🇱🇺',
    'Ungarn': '🇭🇺',
    'Hungary': '🇭🇺',
    'Slowakei': '🇸🇰',
    'Slovakia': '🇸🇰',
    'Slowenien': '🇸🇮',
    'Slovenia': '🇸🇮',
    'Kroatien': '🇭🇷',
    'Croatia': '🇭🇷',
    'Rumänien': '🇷🇴',
    'Romania': '🇷🇴',
    'Bulgarien': '🇧🇬',
    'Bulgaria': '🇧🇬',
    'Estland': '🇪🇪',
    'Estonia': '🇪🇪',
    'Lettland': '🇱🇻',
    'Latvia': '🇱🇻',
    'Litauen': '🇱🇹',
    'Lithuania': '🇱🇹',
    'Albanien': '🇦🇱',
    'Albania': '🇦🇱',
    'Andorra': '🇦🇩',
    'Belarus': '🇧🇾',
    'Bosnien und Herzegowina': '🇧🇦',
    'Bosnia and Herzegovina': '🇧🇦',
    'Island': '🇮🇸',
    'Iceland': '🇮🇸',
    'Liechtenstein': '🇱🇮',
    'Malta': '🇲🇹',
    'Moldau': '🇲🇩',
    'Moldova': '🇲🇩',
    'Monaco': '🇲🇨',
    'Montenegro': '🇲🇪',
    'Nordmazedonien': '🇲🇰',
    'North Macedonia': '🇲🇰',
    'San Marino': '🇸🇲',
    'Serbien': '🇷🇸',
    'Serbia': '🇷🇸',
    'Ukraine': '🇺🇦',
    'Vatikanstadt': '🇻🇦',
    'Vatican City': '🇻🇦',
    'Zypern': '🇨🇾',
    'Cyprus': '🇨🇾',
    'Vereinigte Staaten': '🇺🇸',
    'United States': '🇺🇸',
    'USA': '🇺🇸',
    'Kanada': '🇨🇦',
    'Canada': '🇨🇦',
    'Australien': '🇦🇺',
    'Australia': '🇦🇺',
    'Neuseeland': '🇳🇿',
    'New Zealand': '🇳🇿',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'Indien': '🇮🇳',
    'India': '🇮🇳',
    'Brasilien': '🇧🇷',
    'Brazil': '🇧🇷',
    'Mexiko': '🇲🇽',
    'Mexico': '🇲🇽',
    'Argentinien': '🇦🇷',
    'Argentina': '🇦🇷',
    'Südafrika': '🇿🇦',
    'South Africa': '🇿🇦',
    'Südkorea': '🇰🇷',
    'South Korea': '🇰🇷',
    'Türkei': '🇹🇷',
    'Turkey': '🇹🇷',
    'Russland': '🇷🇺',
    'Russia': '🇷🇺',
    'Israel': '🇮🇱',
    'Ägypten': '🇪🇬',
    'Egypt': '🇪🇬',
    'Afghanistan': '🇦🇫',
    'Algerien': '🇩🇿',
    'Algeria': '🇩🇿',
    'Bangladesch': '🇧🇩',
    'Bangladesh': '🇧🇩',
    'Indonesien': '🇮🇩',
    'Indonesia': '🇮🇩',
    'Iran': '🇮🇷',
    'Irak': '🇮🇶',
    'Iraq': '🇮🇶',
    'Nigeria': '🇳🇬',
    'Pakistan': '🇵🇰',
    'Philippinen': '🇵🇭',
    'Philippines': '🇵🇭',
    'Vietnam': '🇻🇳',
  };
  
  // Try exact match first
  if (countryFlags[country]) {
    return countryFlags[country];
  }
  
  // Try case-insensitive match
  const countryLower = country.toLowerCase();
  for (const [key, flag] of Object.entries(countryFlags)) {
    if (key.toLowerCase() === countryLower) {
      return flag;
    }
  }
  
  // Default to world flag
  return '🌍';
};

const QuestionIconComponent = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTooltip, setShowTooltip] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [question, setQuestion] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  
  // Validation states
  const [errors, setErrors] = useState({
    question: '',
    city: '',
    country: ''
  });
  const [touched, setTouched] = useState({
    question: false,
    city: false,
    country: false
  });

  // Validation functions
  const validateQuestion = (value) => {
    if (!value || !value.trim()) {
      return 'Frage ist erforderlich';
    }
    if (value.length > 1000) {
      return 'Frage darf maximal 1000 Zeichen haben';
    }
    // Check for suspicious patterns (HTML/JS)
    const suspiciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe[^>]*>/gi,
      /<object[^>]*>/gi,
      /<embed[^>]*>/gi,
      /<link[^>]*>/gi,
      /<meta[^>]*>/gi,
      /<style[^>]*>.*?<\/style>/gi
    ];
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(value)) {
        return 'Frage enthält nicht erlaubte Inhalte (HTML/JavaScript)';
      }
    }
    return '';
  };

  const validateCity = (value) => {
    if (value && value.trim() && value.length > 100) {
      return 'Stadt darf maximal 100 Zeichen haben';
    }
    return '';
  };

  const validateCountry = (value) => {
    if (value && value.trim() && value.length > 100) {
      return 'Land darf maximal 100 Zeichen haben';
    }
    return '';
  };

  // Update validation when values change
  const updateValidation = (field, value) => {
    let error = '';
    switch (field) {
      case 'question':
        error = validateQuestion(value);
        break;
      case 'city':
        error = validateCity(value);
        break;
      case 'country':
        error = validateCountry(value);
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleQuestionSubmit = async () => {
    // Mark all fields as touched to show validation errors
    setTouched({
      question: true,
      city: true,
      country: true
    });

    // Validate all fields
    const questionError = validateQuestion(question);
    const cityError = validateCity(city);
    const countryError = validateCountry(country);

    setErrors({
      question: questionError,
      city: cityError,
      country: countryError
    });

    // Check if there are any errors (only question is required)
    if (questionError || cityError || countryError) {
      setSnackbar({
        open: true,
        message: 'Bitte korrigiere die Fehler im Formular.',
        severity: 'error'
      });
      return;
    }

    // Show consent dialog first
    setConsentDialogOpen(true);
  };

  const handleConsentConfirm = async () => {
    setConsentDialogOpen(false);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('question', question.trim());
      if (city.trim()) {
        formData.append('city', city.trim());
      }
      if (country.trim()) {
        formData.append('country', country.trim());
      }
      formData.append('is_anonymous', isAnonymous.toString());

      const response = await axios.post('/community/questions/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbar({
        open: true,
        message: response.data.message || 'Frage erfolgreich eingereicht!',
        severity: 'success'
      });

      // Reset form
      setQuestion('');
      setCity('');
      setCountry('');
      setIsAnonymous(true);
      setShowQuestionDialog(false);
      setErrors({
        question: '',
        city: '',
        country: ''
      });
      setTouched({
        question: false,
        city: false,
        country: false
      });

    } catch (error) {
      console.error('Error submitting question:', error);
      
      let errorMessage = 'Fehler beim Einreichen der Frage. Bitte versuche es erneut.';
      
      if (error.response?.status === 400) {
        errorMessage = error.response.data.detail || 'Ungültige Eingabe.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Die eingegebenen Daten sind ungültig.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Auto-hide tooltip when dialog opens
  useEffect(() => {
    if (showQuestionDialog) {
      setShowTooltip(false);
    }
  }, [showQuestionDialog]);

  const handleCloseDialog = () => {
    setShowQuestionDialog(false);
    setShowTooltip(false);
    
    // Reset form and validation
    setQuestion('');
    setCity('');
    setCountry('');
    setIsAnonymous(true);
    setErrors({
      question: '',
      city: '',
      country: ''
    });
    setTouched({
      question: false,
      city: false,
      country: false
    });
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <QuestionIconContainer showTooltip={showTooltip}>
        <QuestionIconOverlay
          onClick={() => {
            setShowQuestionDialog(true);
            setShowTooltip(false);
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onTouchStart={() => {
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 2000);
          }}
        >
          <HelpIcon sx={{ color: 'white', fontSize: '28px' }} />
        </QuestionIconOverlay>
        
        {showTooltip && (
          <QuestionTooltip isMobile={isMobile}>
            <Typography variant="body2" sx={{ 
              color: 'white', 
              fontWeight: 'bold', 
              mb: 1,
              fontSize: isMobile ? '0.9rem' : '0.875rem'
            }}>
              Frage stellen
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: isMobile ? '0.75rem' : '0.75rem',
              lineHeight: 1.3
            }}>
              Stelle deine Frage zu Cybersicherheit - Experten beantworten sie
            </Typography>
          </QuestionTooltip>
        )}
      </QuestionIconContainer>

      {/* Question Dialog */}
      <Dialog
        open={showQuestionDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
          color: 'white',
          textAlign: 'center',
          py: isMobile ? 2 : 3,
          position: 'relative',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpIcon />
              <Typography variant={isMobile ? "h6" : "h5"} component="div" sx={{ 
                fontWeight: 'bold',
                fontSize: isMobile ? '1.1rem' : '1.5rem'
              }}>
                Frage stellen
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: isMobile ? '0.85rem' : '0.875rem' }}>
            Stelle deine Frage zu Cybersicherheit - Experten beantworten sie
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Question */}
            <TextField
              fullWidth
              required
              label="Deine Frage *"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                setTouched(prev => ({ ...prev, question: true }));
                updateValidation('question', e.target.value);
              }}
              onBlur={() => setTouched(prev => ({ ...prev, question: true }))}
              error={!!errors.question && touched.question}
              helperText={
                errors.question && touched.question 
                  ? errors.question 
                  : `Max. 1000 Zeichen, nur Text erlaubt (keine HTML-Tags) - ${question.length}/1000`
              }
              inputProps={{ maxLength: 1000 }}
              multiline
              rows={4}
            />

            {/* City */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Stadt (optional)
                </Typography>
                <Tooltip 
                  title="Diese Information hilft uns, regionale Trends und Muster bei Cybersicherheitsfragen zu identifizieren und ermöglicht eine bessere geografische Darstellung in unserem Lagebild."
                  arrow
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setTouched(prev => ({ ...prev, city: true }));
                  updateValidation('city', e.target.value);
                }}
                onBlur={() => setTouched(prev => ({ ...prev, city: true }))}
                error={!!errors.city && touched.city}
                helperText={errors.city && touched.city ? errors.city : "Deine Stadt (optional)"}
                inputProps={{ maxLength: 100 }}
              />
            </Box>

            {/* Country */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Land (optional)
                </Typography>
                <Tooltip 
                  title="Diese Information hilft uns, regionale Trends und Muster bei Cybersicherheitsfragen zu identifizieren und ermöglicht eine bessere geografische Darstellung in unserem Lagebild."
                  arrow
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Autocomplete
                fullWidth
                options={COUNTRIES}
                value={country || null}
                onChange={(event, newValue) => {
                  const selectedCountry = newValue || '';
                  setCountry(selectedCountry);
                  setTouched(prev => ({ ...prev, country: true }));
                  updateValidation('country', selectedCountry);
                }}
                onBlur={() => setTouched(prev => ({ ...prev, country: true }))}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>{getCountryFlag(option)}</Typography>
                    <Typography>{option}</Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.country && touched.country}
                    helperText={errors.country && touched.country ? errors.country : "Dein Land (optional)"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {country && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                              <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>{getCountryFlag(country)}</Typography>
                            </Box>
                          )}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                freeSolo={false}
                clearOnEscape
                selectOnFocus
              />
            </Box>

            {/* Show Name Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!isAnonymous}
                  onChange={(e) => setIsAnonymous(!e.target.checked)}
                  color="primary"
                />
              }
              label="Deinen Namen als Autor der Frage im Lagebild anzeigen?"
            />
            {isAnonymous && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2, ml: 4 }}>
                Dein Name wird nicht angezeigt. Die Frage wird als "Anonym" angezeigt.
              </Typography>
            )}
            {!isAnonymous && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2, ml: 4 }}>
                Dein Name wird im Lagebild angezeigt.
              </Typography>
            )}
          </Box>
          
          {/* Info Text */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
            <Typography variant="body2" sx={{ color: 'info.dark', fontSize: '0.875rem', textAlign: 'center' }}>
              Die Antwort auf deine Frage wird nach Prüfung im Ticker auf cyberlagebild.de angezeigt.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: isMobile ? 2 : 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              px: 3,
              py: 1.5
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleQuestionSubmit}
            variant="contained"
            sx={{ 
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              borderRadius: '8px',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              }
            }}
          >
            Frage einreichen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consent Dialog */}
      <Dialog
        open={consentDialogOpen}
        onClose={() => setConsentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5',
          color: '#333',
          textAlign: 'center',
          py: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'normal' }}>
            Einverständniserklärung
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Mit deiner Frage erteilst Du uns deine Einwilligung, dass wir die von dir 
              bekanntgegebenen Daten (Name, Frage, Stadt und Land) zur Bearbeitung deiner Frage verarbeiten sowie für ein Jahr 
              speichern, um allfällige Nachfragen richtig zuordnen und Entwicklungen im 
              Bereich der Cybersicherheit dokumentieren zu können. Du kannst deine 
              Einwilligung jederzeit widerrufen, indem Du dich an uns unter den angegebenen 
              Kontaktdaten (
              <a href="https://www.cyberlagebild.de/contact-2" target="_blank" rel="noopener noreferrer">
                https://www.cyberlagebild.de/contact-2
              </a>
              ) wendest.
            </Typography>
            
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Aus deiner Frage ergeben sich keinerlei Beratungs-, Rechts- oder 
              Veröffentlichungsansprüche. Bei einer Veröffentlichung werden nur die Daten angezeigt, die du selbst eingibst.
            </Typography>
            
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: '#f9f9f9', 
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'normal', color: '#666' }}>
                Hinweis: Durch das Bestätigen stimmst du der Verarbeitung deiner Daten 
                gemäß den oben genannten Bedingungen zu.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setConsentDialogOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              px: 3,
              py: 1.5
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConsentConfirm}
            variant="contained"
            sx={{ 
              background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
              borderRadius: '8px',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              }
            }}
          >
            Einverstanden und Frage einreichen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuestionIconComponent;